import React, { useEffect, useState } from 'react';
import { X, Loader2, Search, ShieldCheck, Home, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

/**
 * Who can work in a branch.
 *
 * A person's home branch (users.location_id) says where they are based — one value, used
 * for defaults and reporting. It cannot answer "which branches may this person see", which
 * is the question a multi-site provider actually asks, so access is a separate list.
 *
 * The rule worth understanding before changing anything here: a staff member with nobody
 * listing them anywhere can see every branch. That is how the product has always worked,
 * and switching thousands of existing staff to "sees nothing" because a table was added
 * would be an outage dressed as a feature. Access narrows only once someone is
 * deliberately given a list.
 */
export default function BranchAccess({ branch, onClose }) {
  const [people, setPeople] = useState([]);
  const [chosen, setChosen] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');

  useEffect(() => {
    let alive = true;
    api.get(`/locations/${branch.id}/access`)
      .then(({ data }) => {
        if (!alive) return;
        setPeople(data);
        setChosen(new Set(data.filter(p => p.has_access).map(p => p.id)));
      })
      .catch(() => toast.error('Could not load branch access'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [branch.id]);

  const toggle = (id) => setChosen(s => {
    const next = new Set(s);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/locations/${branch.id}/access`, { user_ids: [...chosen] });
      toast.success(`Branch access updated — ${chosen.size} staff`);
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not save');
    } finally { setSaving(false); }
  };

  const term = q.trim().toLowerCase();
  const shown = term
    ? people.filter(p => `${p.first_name} ${p.last_name} ${p.email}`.toLowerCase().includes(term))
    : people;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 p-0 sm:p-6"
         onClick={() => onClose(false)}>
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-xl max-h-[88vh] flex flex-col"
           onClick={e => e.stopPropagation()}>

        <div className="flex items-start justify-between gap-4 p-5 border-b border-slate-100">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck size={17} className="text-indigo-600" />
              <h3 className="font-bold text-slate-900">Branch access</h3>
            </div>
            <p className="text-sm text-slate-500 mt-0.5">
              Who can work in <span className="font-semibold text-slate-700">{branch.name}</span>
            </p>
          </div>
          <button onClick={() => onClose(false)} className="p-1 text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 pt-4">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search staff"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm"
            />
          </div>
          <div className="flex items-center justify-between mt-3 text-xs">
            <span className="text-slate-500">
              <Users size={12} className="inline mr-1 -mt-0.5" />
              {chosen.size} of {people.length} selected
            </span>
            <div className="flex gap-3">
              <button onClick={() => setChosen(new Set(people.map(p => p.id)))}
                className="font-semibold text-indigo-600 hover:underline">Select all</button>
              <button onClick={() => setChosen(new Set())}
                className="font-semibold text-slate-500 hover:underline">Clear</button>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-slate-400">
              <Loader2 size={16} className="animate-spin mr-2" /> Loading staff…
            </div>
          ) : shown.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">No staff match that search.</p>
          ) : (
            <div className="space-y-1">
              {shown.map(p => {
                const on = chosen.has(p.id);
                return (
                  <button key={p.id} onClick={() => toggle(p.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border text-left transition-colors ${
                      on ? 'border-indigo-300 bg-indigo-50' : 'border-transparent hover:bg-slate-50'}`}>
                    <span className={`w-4 h-4 rounded border grid place-items-center shrink-0 ${
                      on ? 'bg-indigo-600 border-indigo-600' : 'border-slate-300'}`}>
                      {on && <span className="w-1.5 h-1.5 rounded-sm bg-white" />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-semibold text-slate-800 truncate">
                        {p.first_name} {p.last_name}
                        {p.is_home_branch && (
                          <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-indigo-600">
                            <Home size={9} className="inline -mt-0.5 mr-0.5" />based here
                          </span>
                        )}
                      </span>
                      <span className="block text-xs text-slate-400 truncate">{p.email} · {p.role}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-5 border-t border-slate-100">
          {chosen.size === 0 && !loading && (
            <p className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2 mb-3 leading-snug">
              With nobody selected this branch places no restriction on anyone. Staff who are
              not listed against any branch can still see them all — access narrows only for
              people you deliberately assign.
            </p>
          )}
          <div className="flex gap-2">
            <button onClick={() => onClose(false)}
              className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
            <button onClick={save} disabled={saving || loading}
              className="flex-1 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 inline-flex items-center justify-center gap-2 disabled:opacity-60">
              {saving && <Loader2 size={14} className="animate-spin" />} Save access
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
